import { useCallback, useEffect, useRef, useState } from 'react';
import { loadRaidOverview, loadRaidOverviewLiveState } from '../api/raidOverviewApi.js';
import { subscribeRaidResultLiveSignal } from '../api/raidOverviewLiveSignalApi.js';

const LIVE_REFRESH_MIN_INTERVAL_MS = 5_000;
const EMPTY_OVERVIEW = {
  unionId: '',
  unionName: '',
  viewerRole: '',
  seasons: [],
  selectedSeason: null,
};

export function useRaidOverview() {
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const runRef = useRef(0);
  const overviewRef = useRef(EMPTY_OVERVIEW);
  const selectedSeasonNoRef = useRef(null);
  const requestRef = useRef({ key: '', promise: null });
  const liveRequestRef = useRef(null);
  const lastLiveCheckAtRef = useRef(0);
  const blockingRequestRef = useRef(null);
  const liveTimerRef = useRef(null);
  const liveDirtyRef = useRef(false);
  const activeRef = useRef(true);

  const acceptOverview = useCallback((next) => {
    overviewRef.current = next;
    selectedSeasonNoRef.current = next?.selectedSeason?.seasonNo || null;
    lastLiveCheckAtRef.current = Date.now();
    setOverview(next);
    return next;
  }, []);

  const refresh = useCallback(async (seasonNo = null, { silent = false } = {}) => {
    const requestedSeasonNo = seasonNo == null ? null : Number(seasonNo);
    const normalizedSeasonNo = Number.isFinite(requestedSeasonNo) ? requestedSeasonNo : null;
    const requestKey = normalizedSeasonNo == null ? 'current' : String(normalizedSeasonNo);
    const runId = runRef.current + 1;
    runRef.current = runId;

    let requestPromise = requestRef.current.key === requestKey
      ? requestRef.current.promise
      : null;
    if (!requestPromise) {
      requestPromise = loadRaidOverview(normalizedSeasonNo);
      requestRef.current = { key: requestKey, promise: requestPromise };
    }

    if (!silent) {
      blockingRequestRef.current = requestPromise;
      setLoading(true);
      setErrorMessage('');
    } else if (blockingRequestRef.current) {
      blockingRequestRef.current = requestPromise;
    }

    try {
      const next = await requestPromise;
      if (runRef.current !== runId) return null;
      acceptOverview(next);
      setErrorMessage('');
      return next;
    } catch (error) {
      if (runRef.current === runId) setErrorMessage(error instanceof Error ? error.message : '레이드 현황을 불러오지 못했습니다.');
      return null;
    } finally {
      if (requestRef.current.promise === requestPromise) requestRef.current = { key: '', promise: null };
      if (blockingRequestRef.current === requestPromise) {
        blockingRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [acceptOverview]);

  const refreshLive = useCallback(async function refreshLive(seasonNo = null) {
    const current = overviewRef.current;
    const currentSeasonNo = Number(current?.selectedSeason?.seasonNo || 0);
    const requestedSeasonNo = seasonNo == null ? currentSeasonNo : Number(seasonNo);
    if (!current?.selectedSeason || !currentSeasonNo || requestedSeasonNo !== currentSeasonNo) return null;
    if (!activeRef.current) return null;
    liveDirtyRef.current = true;
    if (liveRequestRef.current) return liveRequestRef.current;
    const remaining = LIVE_REFRESH_MIN_INTERVAL_MS - (Date.now() - lastLiveCheckAtRef.current);
    if (remaining > 0) {
      if (liveTimerRef.current === null) {
        liveTimerRef.current = window.setTimeout(() => {
          liveTimerRef.current = null;
          if (activeRef.current) void refreshLive();
        }, remaining);
      }
      return current;
    }
    if (liveTimerRef.current !== null) window.clearTimeout(liveTimerRef.current);
    liveTimerRef.current = null;
    liveDirtyRef.current = false;

    lastLiveCheckAtRef.current = Date.now();
    const requestPromise = loadRaidOverviewLiveState(current);
    liveRequestRef.current = requestPromise;
    try {
      const next = await requestPromise;
      if (!activeRef.current || overviewRef.current !== current) return overviewRef.current;
      if (next && next !== current) acceptOverview(next);
      return next;
    } catch {
      return null;
    } finally {
      if (liveRequestRef.current === requestPromise) liveRequestRef.current = null;
      if (activeRef.current && liveDirtyRef.current) void refreshLive();
    }
  }, [acceptOverview]);

  useEffect(() => {
    activeRef.current = true;
    refresh();
    const changed = () => refresh(selectedSeasonNoRef.current, { silent: true });
    window.addEventListener('dororaid:raid-overview-changed', changed);
    window.addEventListener('dororaid:raid-season-changed', changed);
    return () => {
      activeRef.current = false;
      liveDirtyRef.current = false;
      if (liveTimerRef.current !== null) window.clearTimeout(liveTimerRef.current);
      liveTimerRef.current = null;
      runRef.current += 1;
      window.removeEventListener('dororaid:raid-overview-changed', changed);
      window.removeEventListener('dororaid:raid-season-changed', changed);
    };
  }, [refresh]);

  useEffect(() => {
    const unionId = String(overview.unionId || '').trim();
    const seasonId = String(overview.selectedSeason?.id || '').trim();
    const liveSeasonNo = Number(overview.selectedSeason?.seasonNo || 0);
    if (!unionId || !seasonId || !liveSeasonNo) return undefined;

    return subscribeRaidResultLiveSignal({
      unionId,
      seasonId,
      seasonNo: liveSeasonNo,
      onChange: () => {
        refreshLive(liveSeasonNo);
      },
    });
  }, [overview.selectedSeason?.id, overview.selectedSeason?.seasonNo, overview.unionId, refreshLive]);

  const selectSeason = useCallback((seasonNo) => {
    const value = Number(seasonNo);
    if (!Number.isInteger(value) || value < 1) return;
    refresh(value);
  }, [refresh]);

  return {
    ...overview,
    loading,
    errorMessage,
    refresh,
    refreshLive,
    selectSeason,
  };
}
